<script setup lang="ts">
import { CreditCard, LifeBuoy, Mail, ShieldCheck } from 'lucide-vue-next';
import SeoHead from '@/components/marketing/SeoHead.vue';
import MarketingLayout from '@/layouts/MarketingLayout.vue';

const cards = [
    {
        icon: LifeBuoy,
        title: 'Product support',
        desc: 'Something broken, confusing, or missing? Tell us — we answer every message, usually within a business day.',
        email: 'support@creativetint.com',
        iconBg: 'from-violet-500 to-fuchsia-500',
    },
    {
        icon: CreditCard,
        title: 'Billing & refunds',
        desc: 'Plan changes, invoices, credit questions, or the 14-day refund on your first payment.',
        email: 'billing@creativetint.com',
        iconBg: 'from-teal-500 to-emerald-500',
    },
    {
        icon: ShieldCheck,
        title: 'Privacy & security',
        desc: 'Data access, export, or deletion requests — and responsible disclosure of security issues.',
        email: 'privacy@creativetint.com',
        iconBg: 'from-amber-500 to-orange-500',
    },
    {
        icon: Mail,
        title: 'Sales & partnerships',
        desc: 'Team plans, agencies with multiple brands, or anything that starts with "what if we…".',
        email: 'hello@creativetint.com',
        iconBg: 'from-indigo-500 to-violet-500',
    },
];
</script>

<template>
    <SeoHead
        title="Contact CreativeTint — Support, Billing & Partnerships"
        description="Talk to a human at CreativeTint. Product support, billing, privacy requests, and partnership inquiries — we answer every message."
        path="/contact"
    />
    <MarketingLayout>
        <div class="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-20">
            <div class="mx-auto max-w-2xl text-center">
                <p class="text-xs font-bold tracking-[0.2em] text-violet-500 dark:text-violet-300 uppercase">Contact</p>
                <h1 class="font-display mt-3 text-4xl font-extrabold tracking-tight md:text-5xl">
                    Talk to a <span class="ct-gradient-text">human</span>
                </h1>
                <p class="mt-5 text-lg leading-relaxed text-muted-foreground">
                    No chatbots guarding the inbox. Pick the address that fits and
                    we'll get back to you — usually within one business day.
                </p>
            </div>

            <div class="mt-14 grid gap-5 md:grid-cols-2">
                <a
                    v-for="c in cards"
                    :key="c.title"
                    :href="`mailto:${c.email}`"
                    class="group rounded-3xl border border-border/60 bg-card/60 p-7 transition duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                >
                    <div
                        class="flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg"
                        :class="c.iconBg"
                    >
                        <component :is="c.icon" class="size-5" />
                    </div>
                    <h2 class="font-display mt-5 text-xl font-bold tracking-tight">{{ c.title }}</h2>
                    <p class="mt-2 text-sm leading-relaxed text-muted-foreground">{{ c.desc }}</p>
                    <div class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-500 dark:text-violet-300 transition group-hover:underline">
                        <Mail class="size-4" /> {{ c.email }}
                    </div>
                </a>
            </div>
        </div>
    </MarketingLayout>
</template>
